import React, { useRef, useState } from 'react';
import { websiteApi } from '../api/website.api';
import { useNotify } from '../context/NotifyContext';
import Modal from './Modal';
import {
  ParsedProductRow,
  ProductCsvMode,
  downloadCsv,
  parseProductCsv,
  presentColumns,
  productCsvFilename,
  productPayload,
  productsToCsv,
} from '../utils/productCsv';

interface ProductImportExportProps {
  websiteId: string;
  sectionId: string;
  /** `categorised` products carry a category name and belong to a category. */
  mode: ProductCsvMode;
  /** Used to name the downloaded file, e.g. the business or section name. */
  label: string;
  /** Called after anything changed, so the caller can refetch its lists. */
  onImported: () => void;
}

interface ImportReport {
  added: number;
  updated: number;
  categoriesAdded: number;
  skipped: string[];
}

const ProductImportExport: React.FC<ProductImportExportProps> = ({
  websiteId,
  sectionId,
  mode,
  label,
  onImported,
}) => {
  const { notify, confirm } = useNotify();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<'export' | 'import' | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [report, setReport] = useState<ImportReport | null>(null);

  const fetchProducts = () =>
    mode === 'categorised'
      ? websiteApi.getProductsForCategory(websiteId, sectionId)
      : websiteApi.getContentItems(websiteId, sectionId);

  const handleExport = async () => {
    setBusy('export');
    try {
      // Fetched fresh rather than reusing the list on screen, which may be
      // filtered to one category — an export should hold everything.
      const products = await fetchProducts();
      downloadCsv(productCsvFilename(label), productsToCsv(products || [], mode));
      notify.success(
        products?.length
          ? `Exported ${products.length} product${products.length === 1 ? '' : 's'}.`
          : 'Exported an empty sheet you can fill in and import.'
      );
    } catch (error: any) {
      notify.error(error.response?.data?.message || 'Could not export products.');
    } finally {
      setBusy(null);
    }
  };

  const applyRows = async (rows: ParsedProductRow[], columns: Set<string>, skipped: string[]) => {
    const existing = (await fetchProducts()) || [];
    const existingIds = new Set<string>(existing.map((product: any) => String(product._id)));

    const categoryIdByName = new Map<string, string>();
    if (mode === 'categorised') {
      const categories = (await websiteApi.getCategoriesForSection(websiteId, sectionId)) || [];
      categories.forEach((category: any) => {
        categoryIdByName.set(String(category.categoryName).toLowerCase(), String(category._id));
      });
    }

    let added = 0;
    let updated = 0;
    let categoriesAdded = 0;

    for (let index = 0; index < rows.length; index += 1) {
      const row = rows[index];
      setProgress({ done: index, total: rows.length });

      try {
        let categoryId = '';
        if (mode === 'categorised') {
          const key = row.category.toLowerCase();
          const known = categoryIdByName.get(key);
          if (known) {
            categoryId = known;
          } else {
            // The model's field is categoryName, so it is sent explicitly here.
            const created = await websiteApi.createContentItem(websiteId, sectionId, {
              categoryName: row.category,
            });
            categoryId = String(created._id);
            categoryIdByName.set(key, categoryId);
            categoriesAdded += 1;
          }
        }

        const payload = productPayload(row, columns);

        if (row.id) {
          if (!existingIds.has(row.id)) {
            skipped.push(`Row ${row.rowNumber}: no product with id ${row.id}. Clear the id to add it as new.`);
            continue;
          }
          if (mode === 'categorised') {
            await websiteApi.updateProduct(websiteId, sectionId, row.id, payload, categoryId);
          } else {
            await websiteApi.updateContentItem(websiteId, sectionId, row.id, payload);
          }
          updated += 1;
        } else {
          if (mode === 'categorised') {
            await websiteApi.createProductWithCategory(websiteId, sectionId, categoryId, payload);
          } else {
            await websiteApi.createContentItem(websiteId, sectionId, payload);
          }
          added += 1;
        }
      } catch (error: any) {
        const message = error.response?.data?.message || 'could not be saved';
        skipped.push(`Row ${row.rowNumber} (${row.text.name}): ${message}`);
      }
    }

    return { added, updated, categoriesAdded };
  };

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared straight away so picking the same file again still fires onChange.
    event.target.value = '';
    if (!file) return;

    if (/\.xlsx?$/i.test(file.name)) {
      notify.error('Save the file as CSV first (File → Save As → CSV), then import it.');
      return;
    }

    let text: string;
    try {
      text = await file.text();
    } catch {
      notify.error('Could not read that file.');
      return;
    }

    const { rows, errors } = parseProductCsv(text, mode);
    if (rows.length === 0) {
      notify.error(errors[0] || 'No products found in that file.');
      if (errors.length > 1) setReport({ added: 0, updated: 0, categoriesAdded: 0, skipped: errors });
      return;
    }

    const toUpdate = rows.filter((row) => row.id).length;
    const toAdd = rows.length - toUpdate;
    const ok = await confirm({
      title: 'Import products',
      message: [
        toAdd > 0 ? `${toAdd} product${toAdd === 1 ? '' : 's'} will be added` : '',
        toUpdate > 0 ? `${toUpdate} will be updated` : '',
      ]
        .filter(Boolean)
        .join(' and ')
        .concat(
          errors.length > 0
            ? `. ${errors.length} row${errors.length === 1 ? '' : 's'} will be skipped because of problems.`
            : '.'
        ),
      confirmLabel: 'Import',
    });
    if (!ok) return;

    setBusy('import');
    setProgress({ done: 0, total: rows.length });
    const skipped = [...errors];
    try {
      const result = await applyRows(rows, presentColumns(text), skipped);
      setReport({ ...result, skipped });
      if (result.added || result.updated) {
        onImported();
        notify.success(`Imported: ${result.added} added, ${result.updated} updated.`);
      } else {
        notify.error('Nothing was imported.');
      }
    } catch (error: any) {
      notify.error(error.response?.data?.message || 'Import failed.');
    } finally {
      setBusy(null);
      setProgress({ done: 0, total: 0 });
    }
  };

  const importing = busy === 'import';

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleExport}
          disabled={busy !== null}
          className="btn-ghost px-3 py-2 text-sm disabled:opacity-50"
        >
          {busy === 'export' ? 'Exporting...' : 'Export CSV'}
        </button>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy !== null}
          className="btn-ghost px-3 py-2 text-sm disabled:opacity-50"
        >
          {importing
            ? `Importing ${progress.done + 1} of ${progress.total}...`
            : 'Import CSV'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          onChange={handleFile}
          className="hidden"
        />
      </div>

      <Modal
        isOpen={report !== null}
        onClose={() => setReport(null)}
        title="Import summary"
        maxWidthClassName="max-w-lg"
      >
        {report && (
          <div className="space-y-4">
            <ul className="text-sm text-nova-text2 space-y-1">
              <li>{report.added} product(s) added</li>
              <li>{report.updated} product(s) updated</li>
              {report.categoriesAdded > 0 && <li>{report.categoriesAdded} new category(ies) created</li>}
              <li>{report.skipped.length} row(s) skipped</li>
            </ul>
            {report.skipped.length > 0 && (
              <div>
                <p className="nova-label">Skipped rows</p>
                <div className="max-h-64 overflow-y-auto rounded-lg border border-nova-border bg-nova-bg2 p-3">
                  <ul className="text-sm text-nova-text2 space-y-1">
                    {report.skipped.map((message, index) => (
                      <li key={index}>{message}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            <button type="button" onClick={() => setReport(null)} className="btn-primary w-full justify-center py-2.5">
              Done
            </button>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ProductImportExport;
